import React, { Fragment, useEffect, useState } from 'react'
import TopHeader from '../../../Components/TopHeader/TopHeader'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Button, Card, CardContent, Chip, Dialog, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useDispatch } from 'react-redux';
import { GET_INCOMPLETE_JOB_BY_ID } from '../../../Redux/Slices/IncompleteServiceSlice/IncompleteServiceSlice.action';
import Constant from '../../../Utils/Constant';
import moment from 'moment';
import { Timeline, TimelineItem, TimelineSeparator, TimelineDot, TimelineConnector, TimelineContent } from '@mui/lab';
import AxiosInstance from '../../../Config/AxiosInstance';
import APILIST from '../../../Config/ApiList';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import CustomSubmitButton from '../../../Components/CustomSubmitButton/CustomSubmitButton';
import CustomTextField from '../../../Components/CustomTextField/CustomTextField';
import { useFormik } from 'formik';
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  PersonOutline as PersonOutlineIcon,
  Translate as TranslateIcon,
  EventNote as EventNoteIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Paid as PaidIcon,
  LocationOn as LocationOnIcon,
  MonetizationOn as MonetizationOnIcon,
  SwapHoriz as SwapHorizIcon,
  CheckCircleOutline as CheckCircleOutlineIcon
} from '@mui/icons-material';

const IncompleteServicesDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [inCompleteJobDetails, setInCompleteJobDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [transferRefundModel, setTransferRefundModel] = useState(false);
  const [actionType, setActionType] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalEstimatedHours, setTotalEstimatedHours] = useState(0);
  const [isTransferRefundStatus, setIsTransferRefundStatus] = useState({
    isRefund: null,
    isTransfer: null
  });

  // Calculate total amount including all nested extra hours
  const calculateTotalAmountWithExtras = (jobDetails) => {
    let total = parseFloat(jobDetails?.amount) || 0;
    let totalHours = parseFloat(jobDetails?.estimated_hours) || 0;

    const sumExtraAmounts = (extraDetails) => {
      if (!extraDetails) return { amount: 0, hours: 0 };

      let extraAmount = parseFloat(extraDetails?.amount) || 0;
      let extraHours = parseFloat(extraDetails?.estimated_hours) || 0;

      if (extraDetails?.extra_hours_details) {
        const nested = sumExtraAmounts(extraDetails.extra_hours_details);
        extraAmount += nested.amount;
        extraHours += nested.hours;
      }

      return { amount: extraAmount, hours: extraHours };
    };

    const extras = sumExtraAmounts(jobDetails?.extra_hours_details);
    total += extras.amount;
    totalHours += extras.hours;

    return { totalAmount: total, totalHours: totalHours };
  };

  useEffect(() => {
    if (location?.state?.inCompleteJobId) {
      const isRefund = location?.state?.is_refund;
      const isTransfer = location?.state?.is_transfer;
      const inCompleteJobId = location?.state?.inCompleteJobId;
      setIsTransferRefundStatus({
        isRefund: isRefund,
        isTransfer: isTransfer
      });
      getInCompleteJobDetails(inCompleteJobId);
    }
  }, [location?.state?.inCompleteJobId]);

  useEffect(() => {
    if (inCompleteJobDetails) {
      const { totalAmount, totalHours } = calculateTotalAmountWithExtras(inCompleteJobDetails);
      setTotalAmount(totalAmount);
      setTotalEstimatedHours(totalHours);
    }
  }, [inCompleteJobDetails]);

  const getInCompleteJobDetails = async (id) => {
    try {
      setIsLoading(true);
      const response = await dispatch(GET_INCOMPLETE_JOB_BY_ID(id));
      if (response && response?.payload?.s) {
        setInCompleteJobDetails(response?.payload?.r);
      } else {
        setInCompleteJobDetails({});
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load job details");
    } finally {
      setIsLoading(false);
    }
  };

  const getTimelineContent = (status) => {
    switch (status) {
      case 1: return 'In Route';
      case 2: return 'Arrived';
      case 3: return 'Start';
      case 4: return 'Pause';
      case 5: return 'Resume';
      case 6: return 'Completed';
      case 7: return 'Incomplete';
      default: return 'Checkout';
    }
  };

  const handleRefundClick = () => {
    setActionType('refund');
    setTransferRefundModel(true);
  };

  const handleTransferClick = () => {
    setActionType('transfer');
    setTransferRefundModel(true);
  };

  const formik = useFormik({
    initialValues: {
      transferHours: "",
      refundHours: "",
    },
    validationSchema: Yup.object().shape({
      transferHours: Yup.number()
        .min(0, "Must be positive")
        .test(
          'total-hours',
          'Total transfer + refund cannot exceed available hours',
          function (value) {
            const { refundHours } = this.parent;
            const totalUsed = (parseFloat(value || 0) + parseFloat(refundHours || 0));
            return totalUsed <= totalEstimatedHours;
          }
        ),
      refundHours: Yup.number()
        .min(0, "Must be positive")
        .test(
          'total-hours',
          'Total transfer + refund cannot exceed available hours',
          function (value) {
            const { transferHours } = this.parent;
            const totalUsed = (parseFloat(value || 0) + parseFloat(transferHours || 0));
            return totalUsed <= totalEstimatedHours;
          }
        ),
    }),
    onSubmit: (values) => {
      const { transferHours, refundHours } = values;

      if (transferHours > 0 && refundHours > 0) {
        Swal.fire({
          title: `Confirm Transfer & Refund`,
          text: `You are about to transfer ${transferHours} hours ($${(totalAmount * transferHours / totalEstimatedHours).toFixed(2)}) and refund ${refundHours} hours ($${(totalAmount * refundHours / totalEstimatedHours).toFixed(2)}).`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#46adf2",
          cancelButtonColor: "#5b5d5d",
          confirmButtonText: `Yes, proceed`,
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed) {
            processTransferAndRefund(transferHours, refundHours);
          }
        });
      } else if (transferHours > 0) {
        // Handle only transfer
        Swal.fire({
          title: `Confirm Transfer`,
          text: `You are about to transfer ${transferHours} hours ($${(totalAmount * transferHours / totalEstimatedHours).toFixed(2)}).`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#46adf2",
          cancelButtonColor: "#5b5d5d",
          confirmButtonText: `Yes, transfer`,
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed) {
            handleTransfer(inCompleteJobDetails?.id, transferHours);
          }
        });
      } else if (refundHours > 0) {
        // Handle only refund
        Swal.fire({
          title: `Confirm Refund`,
          text: `You are about to refund ${refundHours} hours ($${(totalAmount * refundHours / totalEstimatedHours).toFixed(2)}).`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#46adf2",
          cancelButtonColor: "#5b5d5d",
          confirmButtonText: `Yes, refund`,
          cancelButtonText: "Cancel",
        }).then((result) => {
          if (result.isConfirmed) {
            const paymentIntents = getAllPaymentIntents(inCompleteJobDetails);
            handleRefund(paymentIntents, refundHours);
          }
        });
      } else {
        toast.error("Please enter hours for either transfer or refund");
      }
    }
  });

  const processTransferAndRefund = async (transferHours, refundHours) => {
    try {
      setIsLoading(true);

      // First process transfer
      await handleTransfer(inCompleteJobDetails?.id, transferHours);

      // Then process refund
      const paymentIntents = getAllPaymentIntents(inCompleteJobDetails);
      await handleRefund(paymentIntents, refundHours);

      // Refresh data
      getInCompleteJobDetails(inCompleteJobDetails?.id);
      toast.success("Transfer and refund processed successfully");
      setTransferRefundModel(false);
    } catch (error) {
      console.error("Transfer/Refund error:", error);
      toast.error(error.message || "An error occurred while processing");
    } finally {
      setIsLoading(false);
    }
  };
  // Helper function to recursively collect all payment intents
  const getAllPaymentIntents = (jobDetails) => {
    const paymentIntents = [];

    // Add main payment intent if exists
    if (jobDetails?.payment_intent) {
      paymentIntents.push(jobDetails.payment_intent);
    }

    // Recursive function to collect payment intents from extra hours
    const collectExtraPaymentIntents = (extraDetails) => {
      if (!extraDetails) return;

      if (extraDetails?.payment_intent) {
        paymentIntents.push(extraDetails.payment_intent);
      }

      if (extraDetails?.extra_hours_details) {
        collectExtraPaymentIntents(extraDetails.extra_hours_details);
      }
    };

    collectExtraPaymentIntents(jobDetails?.extra_hours_details);
    return paymentIntents;
  };

  // Updated handleRefund to process multiple payment intents
  const handleRefund = async (paymentIntents, hours) => {
    try {
      setIsLoading(true);

      // Process refund for each payment intent
      for (const paymentIntent of paymentIntents) {
        const formData = new FormData();
        formData.append("payment_intent", paymentIntent);
        formData.append("hours", hours);
        formData.append("include_extras", "1");
        formData.append("is_solved", 1)
        const response = await AxiosInstance.post(APILIST.POST_USER_REFUND_AMOUNT, formData);

        if (!response?.data?.s) {
          throw new Error(response?.data?.m || "Refund failed for one or more payments");
        }
      }

      getInCompleteJobDetails(inCompleteJobDetails?.id);
      toast.success("Refund processed successfully for all payments");
      setTransferRefundModel(false);
    } catch (error) {
      console.error("Refund error:", error);
      toast.error(error.message || "An error occurred while processing the refund");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransfer = async (job_id, hours) => {
    try {
      setIsLoading(true);
      const formData = new FormData();

      // Determine which ID to use for transfer
      if (inCompleteJobDetails?.extra_hours_details) {
        // If extra hours exist, use the extra_hours_details ID if available
        const extraHoursId = getDeepestExtraHoursId(inCompleteJobDetails.extra_hours_details);
        formData.append("job_id", extraHoursId || job_id);
        formData.append("is_solved", 1)
      } else if (inCompleteJobDetails?.is_incomplete === 1) {
        // If job is incomplete, use the is_incomplete ID if available
        formData.append("job_id", inCompleteJobDetails.is_incomplete);
        formData.append("is_solved", 1)
      } else {
        // Default to the main job ID
        formData.append("job_id", job_id);
      }

      formData.append("hours", hours);
      formData.append("include_extras", "1"); // Include all extras in transfer

      const response = await AxiosInstance.post(APILIST.POST_USER_TRANSFER_AMOUNT, formData);

      if (response?.data?.s) {
        getInCompleteJobDetails(inCompleteJobDetails?.id);
        toast.success(response.data.m || "Transfer successful");
        setTransferRefundModel(false);
      } else {
        toast.error(response.data.m || "Transfer failed");
      }
    } catch (error) {
      console.error("Transfer error:", error);
      toast.error("An error occurred while processing the transfer");
    } finally {
      setIsLoading(false);
    }
  };
  const getDeepestExtraHoursId = (extraDetails) => {
    if (!extraDetails) return null;

    if (extraDetails?.extra_hours_details) {
      return getDeepestExtraHoursId(extraDetails.extra_hours_details);
    }

    return extraDetails.id;
  };

  // Calculate remaining hours available for transfer/refund
  const calculateRemainingHours = () => {
    const { transferHours, refundHours } = formik.values;
    const usedHours = (parseFloat(transferHours || 0) + parseFloat(refundHours || 0));
    return totalEstimatedHours - usedHours;
  };
  // Helper function to get the deepest extra_hours_details ID
  // const getDeepestExtraHoursId = (extraDetails) => {
  //   if (!extraDetails) return null;

  //   if (extraDetails?.extra_hours_details) {
  //     return getDeepestExtraHoursId(extraDetails.extra_hours_details);
  //   }

  //   return extraDetails.id;
  // };
  const renderExtraHoursDetails = () => {
    if (!inCompleteJobDetails?.extra_hours_details) return null;

    const renderExtraHoursCard = (extraHoursData, level = 0) => {
      return (
        <Grid item xs={12} key={extraHoursData.id}>
          <Card sx={{
            marginTop: 2,
            background: "rgba(230, 245, 247, 1)",
            borderLeft: level > 0 ? "4px solid rgba(0, 151, 178, 0.5)" : "none",
            position: 'relative'
          }}>
            <Box sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              backgroundColor: level === 0 ? '#08557f' : '#0097b2',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              {level === 0 ? 'Primary Extra Hours' : `Additional Request ${level}`}
            </Box>

            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{
                    color: "rgba(8, 85, 127, 1)",
                    fontSize: "16px",
                    fontWeight: "500",
                    fontFamily: "Outfit",
                    marginBottom: 2
                  }}>
                    Request Details
                  </Typography>

                  <DetailItem label="Title" value={extraHoursData?.title} />
                  <DetailItem label="Description" value={extraHoursData?.description} />
                  <DetailItem label="From Language" value={extraHoursData?.from_language} />
                  <DetailItem label="To Language" value={extraHoursData?.to_language} />
                  <DetailItem label="Location" value={extraHoursData?.location} />
                  <DetailItem
                    label="Date & Time"
                    value={extraHoursData?.date_time ?
                      `${moment(extraHoursData.date_time).format('DD/MM/YYYY HH:mm')} - ${moment(extraHoursData.end_time).format('HH:mm')}` :
                      "-"
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{
                    color: "rgba(8, 85, 127, 1)",
                    fontSize: "16px",
                    fontWeight: "500",
                    fontFamily: "Outfit",
                    marginBottom: 2
                  }}>
                    Payment & Status
                  </Typography>

                  <DetailItem label="Estimated Hours" value={`${extraHoursData?.estimated_hours} hours`} />
                  <DetailItem label="Amount" value={`$${extraHoursData?.amount || "0"}`} />
                  <DetailItem label="Payment Status" value={
                    extraHoursData?.payment_status === "succeeded" ?
                      <Chip label="Paid" color="success" size="small" /> :
                      <Chip label="Pending" color="warning" size="small" />
                  } />
                  <DetailItem label="Request Status" value={
                    extraHoursData?.request_status === 1 ?
                      <Chip label="Accepted" color="success" size="small" /> :
                      <Chip label="Pending" color="warning" size="small" />
                  } />
                  <DetailItem label="Job Status" value={
                    <Chip
                      label={getJobStatusLabel(extraHoursData?.job_status)}
                      color={getJobStatusColor(extraHoursData?.job_status)}
                      size="small"
                    />
                  } />
                  <DetailItem label="Service Type" value={
                    extraHoursData?.service_type === 1 ? "Onsite" :
                      extraHoursData?.service_type === 2 ? "Virtual" : "Both"
                  } />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Typography variant="h6" sx={{
                    color: "rgba(8, 85, 127, 1)",
                    fontSize: "16px",
                    fontWeight: "500",
                    fontFamily: "Outfit",
                    marginBottom: 2
                  }}>
                    Activity Timeline
                  </Typography>

                  {extraHoursData?.tracking?.length > 0 ? (
                    <Box sx={{ maxHeight: '200px', overflowY: 'auto', pr: 1 }}>
                      <Timeline sx={{ p: 0 }}>
                        {extraHoursData.tracking.map((track, index) => (
                          <TimelineItem key={track.id} sx={{ "&:before": { flex: 0 } }}>
                            <TimelineSeparator>
                              <TimelineDot sx={{ backgroundColor: "#0097B2" }} />
                              {index < extraHoursData.tracking.length - 1 && <TimelineConnector />}
                            </TimelineSeparator>
                            <TimelineContent sx={{ py: '6px', px: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {getTimelineContent(track.tracking_status)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {moment(track.date_time).format("DD/MM/YYYY HH:mm")}
                              </Typography>
                              {track.user_id === inCompleteJobDetails.user_id && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'primary.main' }}>
                                  (Client)
                                </Typography>
                              )}
                              {track.user_id === inCompleteJobDetails.interpreter_id && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'secondary.main' }}>
                                  (Interpreter)
                                </Typography>
                              )}
                            </TimelineContent>
                          </TimelineItem>
                        ))}
                      </Timeline>
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      No tracking information available
                    </Typography>
                  )}
                </Grid>
              </Grid>

              {extraHoursData?.payment_intent && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0, 151, 178, 0.05)', borderRadius: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                    Payment Details
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Payment Intent" value={extraHoursData.payment_intent} />
                      <DetailItem label="Transaction ID" value={extraHoursData.transaction_id || "-"} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DetailItem label="Commission" value={`$${extraHoursData.commission || "0"}`} />
                      {extraHoursData.transfer_intent && (
                        <DetailItem label="Transfer Intent" value={extraHoursData.transfer_intent} />
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {extraHoursData?.extra_hours_details && (
                <Box sx={{ mt: 2 }}>
                  {renderExtraHoursCard(extraHoursData.extra_hours_details, level + 1)}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      );
    };

    const DetailItem = ({ label, value }) => (
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {label}:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 400 }}>
          {value || "-"}
        </Typography>
      </Box>
    );

    const getJobStatusLabel = (status) => {
      switch (status) {
        case 1: return 'Pending';
        case 2: return 'Accepted';
        case 3: return 'In Progress';
        case 4: return 'Completed';
        case 5: return 'Cancelled';
        case 6: return 'Incomplete';
        default: return 'Unknown';
      }
    };

    const getJobStatusColor = (status) => {
      switch (status) {
        case 1: return 'default';
        case 2: return 'primary';
        case 3: return 'info';
        case 4: return 'success';
        case 5: return 'error';
        case 6: return 'warning';
        default: return 'default';
      }
    };

    return (
      <>
        {renderExtraHoursCard(inCompleteJobDetails.extra_hours_details)}

        {inCompleteJobDetails?.total_extra_jobs_and_hours?.flag === 1 && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Card sx={{ background: "rgba(230, 245, 247, 1)" }}>
              <CardContent>
                <Typography variant="h6" sx={{
                  color: "rgba(8, 85, 127, 1)",
                  fontSize: "16px",
                  fontWeight: "500",
                  fontFamily: "Outfit",
                  mb: 1
                }}>
                  Extra Hours Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DetailItem
                      label="Total Extra Jobs"
                      value={inCompleteJobDetails.total_extra_jobs_and_hours.total_extra_jobs}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailItem
                      label="Total Extra Hours"
                      value={`${inCompleteJobDetails.total_extra_jobs_and_hours.total_extra_hours} hours`}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DetailItem
                      label="Total Additional Amount"
                      value={`$${(
                        parseFloat(inCompleteJobDetails.extra_hours_details?.amount || 0) +
                        parseFloat(inCompleteJobDetails.extra_hours_details?.extra_hours_details?.amount || 0)
                      ).toFixed(2)}`}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </>
    );
  };

  return (
    <Fragment>
      <TopHeader
        title={"Service Details"}
        onClick={() => navigate(-1)}
        type="button"
        showButton={false}
      />
      <Box
        sx={{
          padding: "100px 15px 40px 15px",
          maxWidth: { xs: "100%" },
          margin: "0 auto",
          width: "100%",
        }}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Card
              sx={{
                background: "rgba(230, 245, 247, 1)",
                position: "relative",
              }}
            >
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ borderRadius: '15px', width: '100%' }}>
                      <Typography sx={{
                        color: "rgba(8, 85, 127, 1)",
                        fontSize: { xs: '16px', md: "18px" },
                        fontWeight: "500",
                        fontFamily: "Outfit",
                        paddingLeft: '10px',
                        paddingBottom: '10px'
                      }}>
                        {inCompleteJobDetails?.title || "-"}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '2px 0px 0px 0px' }}>
                        <Typography variant="h6" sx={{
                          color: "rgba(0, 0, 0, 1)",
                          fontSize: { xs: '14px', md: "14px" },
                          fontWeight: "500",
                          fontFamily: "Outfit",
                          paddingLeft: '10px'
                        }}>
                          Location:
                        </Typography>
                        <Typography sx={{ padding: '4px 10px', borderRadius: '22px', fontSize: '12px', fontFamily: "Outfit" }}>
                          {inCompleteJobDetails?.location || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '2px 0px 0px 0px' }}>
                        <Typography variant="h6" sx={{
                          color: "rgba(0, 0, 0, 1)",
                          fontSize: { xs: '14px', md: "14px" },
                          fontWeight: "500",
                          fontFamily: "Outfit",
                          paddingLeft: '10px'
                        }}>
                          Date:
                        </Typography>
                        <Typography sx={{ padding: '4px 10px', borderRadius: '22px', fontSize: '12px', fontFamily: "Outfit" }}>
                          {moment(inCompleteJobDetails?.date_time).local().format('DD/MM/YYYY') || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Typography variant="h6" sx={{
                          color: "rgba(0, 0, 0, 1)",
                          fontSize: { xs: '14px', md: "14px" },
                          fontWeight: "500",
                          fontFamily: "Outfit",
                          paddingLeft: '10px'
                        }}>
                          Language :
                        </Typography>
                        <Typography sx={{ padding: '4px 10px', borderRadius: '22px', fontSize: '12px', fontFamily: "Outfit" }}>
                          {inCompleteJobDetails?.from_language || "N/A"} → {inCompleteJobDetails?.to_language || "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Typography variant="h6" sx={{
                          color: "rgba(0, 0, 0, 1)",
                          fontSize: { xs: '14px', md: "14px" },
                          fontWeight: "500",
                          fontFamily: "Outfit",
                          paddingLeft: '10px'
                        }}>
                          Estimated Hours :
                        </Typography>
                        <Typography sx={{ padding: '4px 10px', borderRadius: '22px', fontSize: '12px', fontFamily: "Outfit" }}>
                          {`${totalEstimatedHours} hr` || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '10px' }}>
                        <Typography variant="h6" sx={{
                          color: "rgba(0, 0, 0, 1)",
                          fontSize: { xs: '14px', md: "14px" },
                          fontWeight: "500",
                          fontFamily: "Outfit",
                          paddingLeft: '10px'
                        }}>
                          Type :
                        </Typography>
                        <Typography sx={{
                          background: "rgba(0, 151, 178, 0.2)",
                          padding: '4px 10px',
                          borderRadius: '22px',
                          fontSize: '12px',
                          fontWeight: '500',
                          fontFamily: "Outfit",
                          width: 'fit-content',
                          color: 'rgba(2, 56, 85, 1)'
                        }}>
                          {inCompleteJobDetails?.service_type ?
                            inCompleteJobDetails?.service_type === 1 ? "Onsite" :
                              inCompleteJobDetails?.service_type === 2 ? "Virtual" : "Both"
                            : "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '10px' }}>
                        <Typography variant="h6" sx={{
                          color: "rgba(0, 0, 0, 1)",
                          fontSize: { xs: '14px', md: "14px" },
                          fontWeight: "500",
                          fontFamily: "Outfit",
                          paddingLeft: '10px'
                        }}>
                          Incompleted By :
                        </Typography>
                        <Typography sx={{
                          background: "rgba(0, 151, 178, 0.2)",
                          padding: '4px 10px',
                          borderRadius: '22px',
                          fontSize: '12px',
                          fontWeight: '500',
                          fontFamily: "Outfit",
                          width: 'fit-content',
                          color: 'rgba(2, 56, 85, 1)'
                        }}>
                          {inCompleteJobDetails?.transfer_intent ? "Interpreter" :
                            inCompleteJobDetails?.refund_intent ? "Client" : "N/A"}
                        </Typography>
                      </Box>
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: '100%',
                        paddingTop: '40px'
                      }}>
                        <Button
                          variant="contained"
                          startIcon={<SwapHorizIcon />}
                          sx={{
                            backgroundColor: "rgba(0, 151, 178, 1)",
                            color: "#fff",
                            fontWeight: '500',
                            fontSize: '14px',
                            textTransform: 'capitalize',
                            borderRadius: '8px',
                            px: 2,
                            py: 1.5,
                            '&:hover': {
                              backgroundColor: "rgba(0, 120, 150, 1)",
                              boxShadow: '0px 2px 5px rgba(0, 151, 178, 0.3)'
                            },
                            width: '200px',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => {
                            setActionType('both');
                            setTransferRefundModel(true);
                          }}
                          disabled={
                            isLoading ||
                            (inCompleteJobDetails?.incomplete_session?.[0]?.is_refund === 1 &&
                              inCompleteJobDetails?.incomplete_session?.[0]?.is_transfer === 1)
                          }
                        >
                          {inCompleteJobDetails?.incomplete_session?.[0]?.is_refund === 1 ||
                            inCompleteJobDetails?.incomplete_session?.[0]?.is_transfer === 1 ?
                            'Processed' : 'Transfer & Refund'}
                        </Button>

                        {(inCompleteJobDetails?.incomplete_session?.[0]?.is_refund === 1 ||
                          inCompleteJobDetails?.incomplete_session?.[0]?.is_transfer === 1) && (
                            <Typography variant="caption" sx={{
                              mt: 1,
                              color: 'rgba(8, 85, 127, 1)',
                              fontSize: '12px',
                              textAlign: 'center'
                            }}>
                              {inCompleteJobDetails?.incomplete_session?.[0]?.is_refund === 1 &&
                                inCompleteJobDetails?.incomplete_session?.[0]?.is_transfer === 1 ?
                                'Both transfer and refund processed' :
                                inCompleteJobDetails?.incomplete_session?.[0]?.is_refund === 1 ?
                                  'Refund already processed' : 'Transfer already processed'}
                            </Typography>
                          )}
                      </Box>
                    </Box>
                  </Grid>
                  {/* <Grid item xs={12} md={2}>
                    <Box sx={{ borderRadius: '15px', width: '100%' }}>
                      <Typography sx={{
                        color: "rgba(8, 85, 127, 1)",
                        fontSize: { xs: '16px', md: "18px" },
                        fontWeight: "500",
                        fontFamily: "Outfit",
                        paddingLeft: '10px',
                        paddingBottom: '10px'
                      }}>
                        Client Name
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        padding: '2px 0px 0px 0px',
                        background: "#FFFFFFCC",
                        padding: "15px",
                        borderRadius: '10px',
                        gap: '10px'
                      }}>
                        {inCompleteJobDetails?.user_details?.profile_img ?
                          <img
                            src={`${Constant.BASE_URL}${inCompleteJobDetails?.user_details?.profile_img}`}
                            alt="interpreter_Profile"
                            style={{
                              width: "70px",
                              height: "70px",
                              objectFit: "cover",
                              borderRadius: "5px",
                              objectPosition: "top",
                            }}
                          /> :
                          <AccountCircleIcon sx={{ fontSize: "70px" }} />
                        }
                        <Typography sx={{
                          fontSize: "16px",
                          fontWeight: "300",
                          color: "#000000",
                          textAlign: "left",
                          width: "100%",
                          fontFamily: "Outfit"
                        }}>
                          {inCompleteJobDetails?.user_details?.full_name || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', paddingTop: '10px' }}>
                        {inCompleteJobDetails?.incomplete_session?.[0]?.is_refund == 0 ? (
                          <Button
                            variant="contained"
                            startIcon={<MonetizationOnIcon />}
                            sx={{
                              backgroundColor: "rgba(0, 151, 178, 1)",
                              color: "#fff",
                              fontWeight: '500',
                              fontSize: '14px',
                              textTransform: 'capitalize',
                              borderRadius: '8px',
                              px: 2,
                              py: 1,
                              '&:hover': {
                                backgroundColor: "rgba(0, 120, 150, 1)",
                                boxShadow: '0px 2px 5px rgba(0, 151, 178, 0.3)'
                              },
                            }}
                            onClick={handleRefundClick}
                            disabled={isLoading}
                          >
                            Request Refund
                          </Button>
                        ) : (
                          <Chip
                            label='Refunded'
                            sx={{
                              color: "rgba(8, 85, 127, 1)",
                              fontSize: { xs: '16px', md: "18px" },
                              fontWeight: "500",
                              fontFamily: "Outfit",
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box sx={{ borderRadius: '15px', width: '100%' }}>
                      <Typography sx={{
                        color: "rgba(8, 85, 127, 1)",
                        fontSize: { xs: '16px', md: "18px" },
                        fontWeight: "500",
                        fontFamily: "Outfit",
                        paddingLeft: '10px',
                        paddingBottom: '10px'
                      }}>
                        Interpreter Name
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        padding: '2px 0px 0px 0px',
                        background: "#FFFFFFCC",
                        padding: "15px",
                        borderRadius: '10px',
                        gap: '10px'
                      }}>
                        {inCompleteJobDetails?.interpreter_details?.profile_img ?
                          <img
                            src={`${Constant.BASE_URL}${inCompleteJobDetails?.interpreter_details?.profile_img}`}
                            alt="interpreter_Profile"
                            style={{
                              width: "70px",
                              height: "70px",
                              objectFit: "cover",
                              borderRadius: "5px",
                              objectPosition: "top",
                            }}
                          /> :
                          <AccountCircleIcon sx={{ fontSize: "70px" }} />
                        }
                        <Typography sx={{
                          fontSize: "16px",
                          fontWeight: "300",
                          color: "#000000",
                          textAlign: "left",
                          width: "100%",
                          fontFamily: "Outfit"
                        }}>
                          {inCompleteJobDetails?.interpreter_details?.full_name || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', paddingTop: '10px' }}>
                        {inCompleteJobDetails?.incomplete_session?.[0]?.is_transfer == 0 ? (
                          <Button
                            variant="contained"
                            startIcon={<SwapHorizIcon />}
                            sx={{
                              backgroundColor: "rgba(0, 151, 178, 1)",
                              color: "#fff",
                              fontWeight: '500',
                              fontSize: '14px',
                              textTransform: 'capitalize',
                              borderRadius: '8px',
                              px: 2,
                              py: 1,
                              '&:hover': {
                                backgroundColor: "rgba(0, 120, 150, 1)",
                                boxShadow: '0px 2px 5px rgba(0, 151, 178, 0.3)'
                              },
                            }}
                            onClick={handleTransferClick}
                            disabled={isLoading}
                          >
                            Request Transfer
                          </Button>
                        ) : (
                          <Chip
                            label='Transferred'
                            sx={{
                              color: "rgba(8, 85, 127, 1)",
                              fontSize: { xs: '16px', md: "18px" },
                              fontWeight: "500",
                              fontFamily: "Outfit",
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grid> */}
                  <Grid item xs={12} md={2}>
                    <Box sx={{ borderRadius: '15px', width: '100%' }}>
                      <Typography sx={{
                        color: "rgba(8, 85, 127, 1)",
                        fontSize: { xs: '16px', md: "18px" },
                        fontWeight: "500",
                        fontFamily: "Outfit",
                        paddingLeft: '10px',
                        paddingBottom: '10px'
                      }}>
                        Client Name
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        padding: '2px 0px 0px 0px',
                        background: "#FFFFFFCC",
                        padding: "15px",
                        borderRadius: '10px',
                        gap: '10px'
                      }}>
                        {inCompleteJobDetails?.user_details?.profile_img ?
                          <img
                            src={`${Constant.BASE_URL}${inCompleteJobDetails?.user_details?.profile_img}`}
                            alt="interpreter_Profile"
                            style={{
                              width: "70px",
                              height: "70px",
                              objectFit: "cover",
                              borderRadius: "5px",
                              objectPosition: "top",
                            }}
                          /> :
                          <AccountCircleIcon sx={{ fontSize: "70px" }} />
                        }
                        <Typography sx={{
                          fontSize: "16px",
                          fontWeight: "300",
                          color: "#000000",
                          textAlign: "left",
                          width: "100%",
                          fontFamily: "Outfit"
                        }}>
                          {inCompleteJobDetails?.user_details?.full_name || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Box sx={{ borderRadius: '15px', width: '100%' }}>
                      <Typography sx={{
                        color: "rgba(8, 85, 127, 1)",
                        fontSize: { xs: '16px', md: "18px" },
                        fontWeight: "500",
                        fontFamily: "Outfit",
                        paddingLeft: '10px',
                        paddingBottom: '10px'
                      }}>
                        Interpreter Name
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        padding: '2px 0px 0px 0px',
                        background: "#FFFFFFCC",
                        padding: "15px",
                        borderRadius: '10px',
                        gap: '10px'
                      }}>
                        {inCompleteJobDetails?.interpreter_details?.profile_img ?
                          <img
                            src={`${Constant.BASE_URL}${inCompleteJobDetails?.interpreter_details?.profile_img}`}
                            alt="interpreter_Profile"
                            style={{
                              width: "70px",
                              height: "70px",
                              objectFit: "cover",
                              borderRadius: "5px",
                              objectPosition: "top",
                            }}
                          /> :
                          <AccountCircleIcon sx={{ fontSize: "70px" }} />
                        }
                        <Typography sx={{
                          fontSize: "16px",
                          fontWeight: "300",
                          color: "#000000",
                          textAlign: "left",
                          width: "100%",
                          fontFamily: "Outfit"
                        }}>
                          {inCompleteJobDetails?.interpreter_details?.full_name || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Timeline sx={{
                  background: "#F8F8F8",
                  padding: '20px 20px',
                  borderRadius: '26px',
                  boxSizing: 'border-box'
                }}>
                  {inCompleteJobDetails?.tracking?.map((item, index) => (
                    <TimelineItem key={item.id} sx={{ "&:before": { flex: 0 } }}>
                      <TimelineSeparator sx={{ ".MuiTimelineConnector-root": { backgroundColor: "#0097B2" } }}>
                        <TimelineDot sx={{ backgroundColor: "#0097B2" }} />
                        {index < inCompleteJobDetails?.tracking?.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <Typography sx={{ fontWeight: '500', fontSize: '14px', color: '#000' }}>
                          {getTimelineContent(item?.tracking_status)}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#555' }}>
                          {moment(item?.date_time).format("DD/MM/YYYY")} | {moment(item?.date_time).format("MM:ss A")}
                        </Typography>
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </Timeline>
              </Grid>
              <Grid item xs={12} md={9}>
                <Box>
                  <Grid container spacing={2}>
                    {inCompleteJobDetails?.incomplete_session?.length > 0 ?
                      inCompleteJobDetails?.incomplete_session?.map((elem, index) => {
                        return (
                          <Grid item xs={12} md={6} key={index}>
                            <Box sx={{
                              background: "#F8F8F8",
                              padding: '20px',
                              borderRadius: '20px'
                            }}>
                              <Typography sx={{
                                fontSize: '18px',
                                fontWeight: '300',
                                fontFamily: 'Outfit',
                                textAlign: 'left'
                              }}>
                                Reason :
                              </Typography>
                              <Typography sx={{
                                color: 'rgba(8, 85, 127, 1)',
                                fontSize: '18px',
                                fontWeight: '400',
                                fontFamily: 'Outfit',
                                textAlign: 'left'
                              }}>
                                {elem?.reason || "--"}
                              </Typography>
                              <Typography sx={{
                                fontSize: '18px',
                                fontWeight: '300',
                                fontFamily: 'Outfit',
                                textAlign: 'left'
                              }}>
                                Additional Details :
                              </Typography>
                              <Typography sx={{
                                color: 'rgba(8, 85, 127, 1)',
                                fontSize: '18px',
                                fontWeight: '400',
                                fontFamily: 'Outfit',
                                textAlign: 'left'
                              }}>
                                {elem?.details || "--"}
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      }) : null}
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {renderExtraHoursDetails()}
        </Grid>
      </Box>
      <Dialog
        open={transferRefundModel}
        onClose={() => {
          formik.resetForm();
          setTransferRefundModel(false);
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "20px",
            padding: '20px 30px',
            height: "auto",
            overflow: "scroll",
            "::-webkit-scrollbar": { width: "0px", height: '0px' },
            display: "flex",
            flexDirection: "column",
          },
          "& .MuiDialogContent-root": {
            overflowY: "initial",
            flexGrow: 1,
          },
          "& .MuiBackdrop-root": {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          }
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography sx={{ fontSize: '20px', fontWeight: '500', color: '#101010', fontFamily: "Outfit" }}>
                Transfer & Refund
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <IconButton
                  sx={{
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#101010',
                    borderRadius: '8px',
                    "&:hover": { backgroundColor: '#101010', color: '#fff' }
                  }}
                  onClick={() => {
                    formik.resetForm();
                    setTransferRefundModel(false);
                  }}
                >
                  <CloseIcon sx={{ color: '#fff', fontSize: '20px' }} />
                </IconButton>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ padding: '20px 0px 0px 0px', flexGrow: 1 }}>
            <Typography sx={{
              color: "rgba(0, 0, 0, 1)",
              fontWeight: "400",
              textAlign: "left",
              fontSize: { xs: "16px", sm: "18px" },
              fontFamily: "Outfit",
              marginBottom: '10px'
            }}>
              Total Amount (including extras): ${totalAmount.toFixed(2)}
            </Typography>
            <Typography sx={{
              color: "rgba(0, 0, 0, 1)",
              fontWeight: "400",
              textAlign: "left",
              fontSize: { xs: "16px", sm: "18px" },
              fontFamily: "Outfit",
              marginBottom: '20px'
            }}>
              Total Estimated Hours: {totalEstimatedHours} hours
            </Typography>

            <Grid container spacing={2} justifyContent={'space-between'}>
              <Grid item xs={12}>
                <CustomTextField
                  label={`Hours to Transfer ${inCompleteJobDetails?.interpreter_details?.full_name}`}
                  type='number'
                  step="0.1"
                  placeholder={`Enter transfer hours (max ${totalEstimatedHours})`}
                  name="transferHours"
                  value={formik.values.transferHours}
                  onBlur={formik.handleBlur}
                  onKeyUp={formik.handleBlur}
                  errors={formik.touched.transferHours && formik.errors.transferHours}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value.trimStart() !== value) {
                      e.target.value = value.trimStart();
                    }
                    formik.handleChange(e);
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ color: 'rgba(0, 151, 178, 1)', fontSize: '14px' }}>
                        {formik.values.transferHours > 0 ?
                          `$${(totalAmount * formik.values.transferHours / totalEstimatedHours).toFixed(2)}` : ''
                        }
                      </Typography>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <CustomTextField
                  label={`Hours to Refund ${inCompleteJobDetails?.user_details?.full_name}`}
                  type='number'
                  step="0.1"
                  placeholder={`Enter refund hours (max ${totalEstimatedHours})`}
                  name="refundHours"
                  value={formik.values.refundHours}
                  onBlur={formik.handleBlur}
                  onKeyUp={formik.handleBlur}
                  errors={formik.touched.refundHours && formik.errors.refundHours}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value.trimStart() !== value) {
                      e.target.value = value.trimStart();
                    }
                    formik.handleChange(e);
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography sx={{ color: 'rgba(0, 151, 178, 1)', fontSize: '14px' }}>
                        {formik.values.refundHours > 0 ?
                          `$${(totalAmount * formik.values.refundHours / totalEstimatedHours).toFixed(2)}` : ''
                        }
                      </Typography>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{
                  color: "rgba(0, 0, 0, 1)",
                  fontWeight: "500",
                  textAlign: "left",
                  fontSize: { xs: "16px", sm: "18px" },
                  fontFamily: "Outfit",
                  mb: 1
                }}>
                  Remaining Hours: {calculateRemainingHours().toFixed(1)}
                </Typography>
                <Typography sx={{
                  color: "rgba(0, 0, 0, 0.7)",
                  fontWeight: "400",
                  textAlign: "left",
                  fontSize: { xs: "14px", sm: "15px" },
                  fontFamily: "Outfit"
                }}>
                  Note: The sum of transfer and refund hours cannot exceed the total available hours.
                </Typography>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ paddingTop: '20px' }}>
            <CustomSubmitButton
              btnTitle="Process"
              type="submit"
              disabled={!formik.isValid || formik.isSubmitting || isLoading ||
                (formik.values.transferHours <= 0 && formik.values.refundHours <= 0)}
              loading={isLoading}
            />
          </Box>
        </form>
      </Dialog>
    </Fragment>
  )
}

export default IncompleteServicesDetails;
